import { UserCell } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import ChatEmpty from "@images/empty/chat_empty.svg"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { MemberItemEntity } from "@modules/members/entities/memberEntity"
import { MemberColumnToSort, MemberFilter } from "@modules/members/gateway/memberDto"
import { usePersistedMembers } from "@modules/members/hooks/useMember"
import { useMemberSideBar } from "@modules/members/hooks/useMemberSidebar"
import { convertLastVisitedToColor } from "@modules/members/memberUtils"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { HeaderTemplate } from "@modules/ui/components/dataTables/templates/headerTemplate"
import { EmptyView } from "@modules/ui/components/emptyView"
import { HuNoshow } from "@modules/ui/components/huNoshow"
import { HuNote } from "@modules/ui/components/huNote"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useEffect, useState } from "react"

type Props = {
  memberTab: MemberFilter
  setCountTab: (count: number) => void
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<MemberItemEntity>()

export function MemberTable({ memberTab, setCountTab, className, ...props }: Props) {
  const [selectedMember, setSelectedMember] = useState<MemberItemEntity>()
  const { navigateToMemberDetailsInfo } = useMemberSideBar()

  const {
    data,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    totalElements,
    setSorting,
    sortBy,
    sortOrder,
    isSearching,
  } = usePersistedMembers(memberTab)

  useEffect(() => {
    if (totalElements) {
      setCountTab(totalElements)
    } else setCountTab(0)
  }, [totalElements])

  const columns = [
    columnHelper.accessor("username", {
      header: () => (
        <HeaderTemplate
          title={word("member.label.name")}
          variant={
            sortBy === MemberColumnToSort.NAME
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(MemberColumnToSort.NAME, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(MemberColumnToSort.NAME, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <UserCell
          member={{
            avatar: member.avatar,
            username: member.username,
            firstname: member.firstname,
            lastname: member.lastname,
          }}
          subtitle={member.getUserName()}
          subtitleColor="neutral"
        />
      ),
    }),
    columnHelper.accessor("activeOffers", {
      header: () => (
        <HeaderTemplate
          title={word("box.member.table.activeOffer.label", { count: 1 })}
          variant={
            sortBy === MemberColumnToSort.ACTIVE_OFFER
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(MemberColumnToSort.ACTIVE_OFFER, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(MemberColumnToSort.ACTIVE_OFFER, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <div className="flex flex-row items-center">
          {member.activeOffers.length > 0 ? (
            <Text fontStyle="BM" className="flex text-textDefault-body">
              {member.activeOffers.map((activeOffer, offerIndex) => {
                const warning = activeOffer.getWarning()
                return (
                  <div key={offerIndex} className="flex flex-row gap-1">
                    {warning && (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <WarningIcon
                              color="var(--color-red-500)"
                              width={20}
                              height={20}
                              className="mr-1 shrink-0"
                            />
                          }
                        />
                        <TooltipContent side="bottom" className="w-fit max-w-60">
                          <Text fontStyle="BM" className="text-justify text-textDefault-body">
                            {warning.warningContent}
                          </Text>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <span className="mr-1">
                      {`${activeOffer.offerName}${offerIndex < member.activeOffers.length - 1 ? "," : ""}`}
                    </span>
                  </div>
                )
              })}
            </Text>
          ) : (
            <div className="mr-1 flex flex-row items-center gap-1">
              <WarningIcon color="var(--color-red-500)" width={24} height={24} className="shrink-0" />
              <Text>{word("box.member.table.activeOffer.empty")}</Text>
            </div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor("lastVisited", {
      header: () => (
        <HeaderTemplate
          title={word("box.member.table.lastVisit")}
          variant={
            sortBy === MemberColumnToSort.LAST_VISIT
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(MemberColumnToSort.LAST_VISIT, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(MemberColumnToSort.LAST_VISIT, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) =>
        member.lastVisited ? (
          <Text fontStyle="TS" className={cn("text-textDefault-body", convertLastVisitedToColor(member.lastVisited))}>
            {formatDateWithHyphen(dayjs(member.lastVisited).format("L"))}
          </Text>
        ) : (
          <Text fontStyle="TS" className="text-textDefault-body">
            {word("box.member.table.noVisitYet")}
          </Text>
        ),
    }),
    columnHelper.accessor("note", {
      header: () => <HeaderTemplate title={word("box.member.table.note")} />,
      cell: ({ row: { original: member } }) => (
        <HuNote id={member.id} index={member.id} note={member.note ? member.note : ""} />
      ),
    }),
    columnHelper.accessor("noShowPastMonth", {
      header: () => (
        <HeaderTemplate
          title={word("box.member.table.noShow", { count: 0 })}
          variant={
            sortBy === MemberColumnToSort.NO_SHOW_COUNT
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(MemberColumnToSort.NO_SHOW_COUNT, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(MemberColumnToSort.NO_SHOW_COUNT, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => <HuNoshow number={member.noShowPastMonth || 0} />,
    }),
  ]

  const onSelectedMember = (e: MemberItemEntity) => {
    navigateToMemberDetailsInfo(e.id, {
      onHide: () => {
        setSelectedMember(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    })
    setSelectedMember(e)
  }

  if (data.length === 0)
    return (
      <EmptyView
        image={{ src: ChatEmpty }}
        title={isSearching ? word("searching.list.empty.title") : word("member.list.empty.title")}
        description={isSearching ? word("searching.list.empty.description") : word("member.list.empty.description")}
        paddingType="In_table"
      />
    )

  return (
    <div className={cn("flex flex-col px-6", className)} {...props}>
      <HUDatatable
        data={data}
        columns={columns}
        pageSize={pageSize}
        setPageSize={setPageSize}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handleRowSelectionChange={(row: MemberItemEntity) => onSelectedMember(row)}
        selectedRowIndex={data.findIndex((item) => item.id === selectedMember?.id)}
        totalElements={totalElements}
      />
    </div>
  )
}
